import { Link } from "react-router-dom";

const services = [
  {
    id: "individual",
    icon: "ri-user-smile-line",
    title: "Individual Counseling",
    tagline: "Your story, your pace.",
    description: "Individual therapy is a one-on-one, confidential space where you can explore what's weighing on you — without judgment, without pressure. Whether you're dealing with a specific challenge or simply feeling stuck, we work together to understand the patterns, beliefs, and experiences shaping your life.",
    whatToExpect: [
      "A warm, non-judgmental space to be fully yourself",
      "Personalized goals and a flexible, collaborative approach",
      "Tools and insights you can use between sessions",
      "Consistent support as you grow and change",
    ],
    image: "/images/svc-ind2.jpg",
    color: "bg-[#e6f6f5]",
    accent: "#418b86",
  },
  {
    id: "anxiety",
    icon: "ri-heart-pulse-line",
    title: "Anxiety & Stress Management",
    tagline: "Find calm in the chaos.",
    description: "Anxiety can feel like a constant hum of worry, a racing mind, or a body that won't relax. Stress can leave you depleted, reactive, and disconnected from what matters most. Together, we'll explore the roots of your anxiety and build practical, lasting strategies to quiet the noise and reclaim your sense of ease.",
    whatToExpect: [
      "Understanding the triggers and patterns behind your anxiety",
      "Evidence-based techniques: CBT, mindfulness, breathing practices",
      "Strategies for managing panic, worry, and overwhelm",
      "Building resilience for long-term wellbeing",
    ],
    image: "/images/svc-anx2.jpg",
    color: "bg-[#eaf7f9]",
    accent: "#4db8b1",
  },
  {
    id: "transitions",
    icon: "ri-compass-3-line",
    title: "Life Transitions",
    tagline: "Navigate change with confidence.",
    description: "Change — even positive change — can feel destabilizing. Career shifts, relationship changes, loss, becoming a parent, moving, or simply entering a new chapter of life can leave you feeling unmoored. Therapy provides a steady anchor as you find your footing and move forward with intention.",
    whatToExpect: [
      "Processing grief, uncertainty, and identity shifts",
      "Clarifying your values and what you want next",
      "Building confidence and adaptability",
      "Creating a vision for the life you want to build",
    ],
    image: "/images/svc-trans2.jpg",
    color: "bg-[#f0f9f9]",
    accent: "#418b86",
  },
  {
    id: "relationships",
    icon: "ri-team-line",
    title: "Relationship Support",
    tagline: "Build connections that nourish you.",
    description: "Relationships — with partners, family, friends, or colleagues — are central to our wellbeing. When they're struggling, everything feels harder. Whether you're navigating conflict, communication breakdowns, boundaries, or patterns that keep repeating, therapy can help you understand yourself better and build healthier, more fulfilling connections.",
    whatToExpect: [
      "Exploring relationship patterns and attachment styles",
      "Developing communication and boundary-setting skills",
      "Processing past relational wounds",
      "Building the relationships you actually want",
    ],
    image: "/images/svc-rel2.jpg",
    color: "bg-[#e6f4f6]",
    accent: "#4db8b1",
  },
];

export default function ServicesList() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col gap-20">
          {services.map((service, i) => (
            <div
              key={service.id}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                i % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}
            >
              {/* Image */}
              <div className={`${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="rounded-2xl overflow-hidden h-72 md:h-96">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Content */}
              <div className={`${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className={`inline-flex items-center gap-2 ${service.color} px-4 py-2 rounded-full mb-5`}>
                  <i className={`${service.icon} text-base`} style={{ color: service.accent }}></i>
                  <span className="text-xs font-medium" style={{ color: service.accent }}>{service.tagline}</span>
                </div>

                <h2 className="text-2xl md:text-3xl font-light text-[#2d2d2d] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {service.title}
                </h2>

                <p className="text-[#6b6b6b] text-sm leading-relaxed mb-6">
                  {service.description}
                </p>

                <div className="mb-7">
                  <p className="text-[#2d2d2d] text-xs font-semibold uppercase tracking-wider mb-3">What to Expect</p>
                  <ul className="flex flex-col gap-2.5">
                    {service.whatToExpect.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-[#5a5a5a]">
                        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#e6f6f5] flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-[#4db8b1] text-xs"></i>
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-[#418b86] hover:bg-[#4db8b1] text-white text-sm font-medium px-6 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap"
                >
                  Get Started
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
